import { BigNumber } from '@0x/utils'
import { Asset } from 'bzx-common/src/lib/assetUtils'
import errorUtils from 'app-lib/errorUtils'
import stakingUtils from 'app-lib/stakingUtils'
import { stakeableToken } from 'bzx-common/src/domain/staking/stakingTypes'
import * as mobx from 'mobx'
import RootStore from '../RootStore'
import SpendingAllowance from './SpendingAllowance'
import UserBalances from './UserBalances'

type StakingAllowancesProp = 'checkError'

export default class StakingAllowances {
  [name: string]: any
  private rootStore: RootStore
  private userBalances: UserBalances
  public ooki: SpendingAllowance
  public vbzrx: SpendingAllowance
  public iooki: SpendingAllowance
  public slp_ooki_eth: SpendingAllowance
  public checkError: Error | null = null

  public get error() {
    return (
      this.ooki.error ||
      this.vbzrx.error ||
      this.iooki.error ||
      this.slp_ooki_eth.error ||
      this.checkError ||
      null
    )
  }

  public get list() {
    return [this.ooki, this.vbzrx, this.iooki, this.slp_ooki_eth]
  }

  public get allowanceChecked() {
    return this.list.every((allowance) => allowance.checked)
  }

  public get needApprovalList() {
    const { wallet } = this.userBalances
    return this.list.filter((allowance) => {
      const token = allowance.asset.toLowerCase()
      return allowance.checked && wallet[token].gt(0.01) && allowance.amount.lt(wallet[token])
    })
  }

  /**
   * TODO: refactor, probably dont need both list as array or map
   */
  public get needApprovals(): Map<stakeableToken, SpendingAllowance> {
    const map = new Map<stakeableToken, SpendingAllowance>()
    this.needApprovalList.forEach((allowance) => {
      map.set(allowance.asset.toLocaleLowerCase() as stakeableToken, allowance)
    })
    return map
  }

  /**
   * Helper to set values through mobx actions.
   */
  public set(prop: StakingAllowancesProp, value: any) {
    ;(this[prop] as any) = value
  }

  public clearError() {
    this.list.forEach((approval) => (approval.error = null))
    this.checkError = null
  }

  clearData() {
    const initState = { amount: new BigNumber(0), checked: false }
    this.ooki.assign(initState)
    this.vbzrx.assign(initState)
    this.iooki.assign(initState)
    this.slp_ooki_eth.assign(initState)
    this.clearError()
  }

  public async check() {
    try {
      this.set('checkError', null)
      const fp = this.rootStore.fulcrumProvider
      const result = await stakingUtils.getStakeableAllowances(fp)
      this.ooki.assign({ amount: result.ooki, checked: true })
      this.vbzrx.assign({ amount: result.vbzrx, checked: true })
      this.iooki.assign({ amount: result.iooki, checked: true })
      this.slp_ooki_eth.assign({ amount: result.slp_ooki_eth, checked: true })
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Could not check spending allowances' })
      this.set('checkError', err)
    }
  }

  constructor(userBalances: UserBalances, rootStore: RootStore) {
    this.userBalances = userBalances
    this.rootStore = rootStore
    this.ooki = new SpendingAllowance(Asset.OOKI, this.rootStore.fulcrumProvider)
    this.vbzrx = new SpendingAllowance(Asset.vBZRX, this.rootStore.fulcrumProvider)
    this.iooki = new SpendingAllowance(Asset.IOOKI, this.rootStore.fulcrumProvider)
    this.slp_ooki_eth = new SpendingAllowance(Asset.SLP_OOKI_ETH, this.rootStore.fulcrumProvider)
    mobx.makeAutoObservable(this, undefined, { autoBind: true, deep: false })
  }
}
